import { ChevronRight } from 'lucide-react';
import ExtensionStatus from '../components/ExtensionStatus.jsx';
import LiveIndicator from '../components/LiveIndicator.jsx';
import Pill from '../components/Pill.jsx';
import UpdatedAt from '../components/UpdatedAt.jsx';
import UsageBar from '../components/UsageBar.jsx';
import { useLiveData } from '../lib/useLiveData.js';
import { useLiveStatus } from '../lib/useLiveStatus.js';
import { useRouter } from '../lib/router.jsx';
import { useSession, ROLES } from '../lib/session.jsx';

/* The upstream extension record carries SIP secrets and passwords. The adapter
   whitelists fields onto the wire, so there is nothing here to render by
   accident — see VitalPbxAdapter::mapExtension.

   The list itself (names, recording config) barely changes, so it's polled
   slowly. The status dots are the live part: they're pushed over SSE, so a
   handset registering, dropping or answering a call moves the dot at once
   rather than on the next poll. */
export default function Extensions() {
  const { role } = useSession();
  const { navigate } = useRouter();
  const { status, data, error, updatedAt, refresh } = useLiveData('/extensions', { intervalMs: 120000 });
  const live = useLiveStatus();

  /* Allowances come from WHMCS by way of our own metering, not from the PBX —
     the phone system knows nothing about what was sold. Fetched separately and
     joined here by extension rather than folded into /extensions, which keeps
     the PBX adapter clear of billing. It refreshes slowly because the figures
     are only ever as fresh as the last cron run, and a failure here must not
     take the roster down: the column just goes quiet. */
  const minutes = useLiveData('/minutes', { intervalMs: 300000 });
  const bundlesByExtension = Object.fromEntries(
    (minutes.data?.items ?? []).map((i) => [i.extension, i.bundles]),
  );
  const anyBundles = Object.values(bundlesByExtension).some((b) => b.length > 0);

  const isUser = role === ROLES.EXTENSION_USER;

  if (status === 'loading') {
    return <div className="card"><div className="empty"><p className="empty__body">Loading…</p></div></div>;
  }

  if (status === 'error') {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">We couldn't load your extensions</div>
          <p className="empty__body">{error?.message}</p>
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <LiveIndicator connected={live.connected} supported={live.supported} />
        <UpdatedAt at={updatedAt} onRefresh={refresh} stale={Boolean(error)} />
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table table--sized">
            <colgroup>
              {COLUMNS(anyBundles).map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            {/* No Voicemail column: the platform doesn't tell us whether it's
                enabled, and a guess is worse than an omission. */}
            <thead>
              <tr>
                <th>Ext.</th>
                <th>Name</th>
                <th>Status</th>
                {anyBundles && <th>Allocated minutes</th>}
                <th>Call recording</th>
                <th>Voicemail to email</th>
                <th aria-label="View" />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr
                  key={e.extension}
                  onClick={() => navigate(`/extensions/${e.extension}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="td--mono" style={{ fontWeight: 500, color: 'var(--signal-deep)' }}>{e.extension}</td>
                  <td className="td--truncate" style={{ fontWeight: 600 }} title={e.name}>{e.name}</td>
                  {/* Prefer the pushed status; fall back to what the list
                      fetch carried before the stream connected (or if it's off). */}
                  <td><ExtensionStatus status={live.byExtension[e.extension] ?? e.status} device={e.device} /></td>
                  {anyBundles && (
                    /* Only the bundles actually sold on this extension's plan.
                       A product offers UK, UK/EU and UK/EU/US side by side and
                       the customer buys one — showing the unsold two would
                       promise allowances they don't have. The backend filters
                       them out. */
                    <td onClick={(ev) => ev.stopPropagation()} style={{ cursor: 'default' }}>
                      <ExtensionMinutes bundles={bundlesByExtension[e.extension]} />
                    </td>
                  )}
                  <td>
                    <Pill tone={recordingOn(e.recording) ? 'online' : 'offline'}>
                      {recordingSummary(e.recording)}
                    </Pill>
                  </td>
                  <td
                    className="td--soft td--truncate"
                    style={{ fontSize: 13 }}
                    title={e.voicemail_to_email ?? undefined}
                  >
                    {e.voicemail_to_email ?? '—'}
                  </td>
                  <td className="td--icon">
                    <ChevronRight size={16} color="var(--ink-soft)" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="footnote">
        {isUser
          ? "You're seeing your own extension."
          : 'Choose an extension to see its calls and voicemail.'}
      </p>
    </div>
  );
}

/* Column widths, as percentages summing to 100.

   Percentages rather than pixels because the table is fluid: fixed widths
   leave a remainder, and the browser hands the whole of it to one column,
   which is how "Voicemail to email" — a long header over a column of dashes —
   ended up wider than the names.

   The weighting follows what each column actually has to show. Allocated
   minutes carries a labelled bar and gets the most; status is a dot and a
   word, so it gets little; voicemail needs enough for an email address and no
   more, with the rest truncated onto its title. */
const COLUMNS = (withMinutes) => (withMinutes
  ? ['8%', '26%', '10%', '26%', '13%', '14%', '3%']
  : ['9%', '32%', '13%', '18%', '24%', '4%']);

/* The allowances on one extension's plan, or a quiet dash when it has none —
   an Enterprise line with no bundle attached, or a roster that loaded before
   the minute figures did. */
function ExtensionMinutes({ bundles }) {
  if (!bundles || bundles.length === 0) {
    return <span className="td--soft" style={{ fontSize: 13 }}>—</span>;
  }

  return bundles.map((b, i) => (
    <div key={b.key} style={{ marginTop: i === 0 ? 0 : 10 }}>
      <UsageBar measure={b} coverage={b.coverage} title={b.coverage_description} compact />
    </div>
  ));
}

const REC_KINDS = ['inbound', 'outbound', 'internal'];

function recordingOn(rec) {
  return Boolean(rec) && REC_KINDS.some((k) => rec[k]);
}

function recordingSummary(rec) {
  if (!rec) return 'Unknown';
  const on = REC_KINDS.filter((k) => rec[k]);
  if (on.length === 0) return rec.on_demand ? 'On demand' : 'Off';
  if (on.length === REC_KINDS.length) return 'All calls';
  return on.map((k) => k[0].toUpperCase() + k.slice(1)).join(', ');
}
